import React, { useCallback, useState } from "react";
import { useRecoilValue } from "recoil";

import { AllstarUser } from "MVVM/State";
import { useUser } from "MVVM/Hooks/useUser";
import { usePortal } from "MVVM/Hooks/usePortal";
import { EPriceTabView } from "MVVM/@types";
import { STORAGE_ADDON_ELIGIBLE_TIERS } from "MVVM/Utilities/SubscriptionTiers";
import { StorageAddonModal } from "MVVM/Components/Modals/StorageAddonModal";

interface StorageAddOnPickerProps {
  children: (props: {
    onClick: (options?: {
      plan?: string;
      billingPeriod?: EPriceTabView;
    }) => void;
    isEligible: boolean;
  }) => React.ReactNode;
  analyticsSource: string;
  onBeforeOpen?: () => void;
  onSelect?: () => void;
}

export const StorageAddOnPicker = ({
  children,
  analyticsSource,
  onBeforeOpen,
  onSelect,
}: StorageAddOnPickerProps) => {
  const { allstarUser } = useUser();
  const { loggedIn } = useRecoilValue(AllstarUser);

  const userGroup = allstarUser?.user?.userGroup?.type;
  const isEligible =
    loggedIn && userGroup && STORAGE_ADDON_ELIGIBLE_TIERS.includes(userGroup);

  const [initialPlan, setInitialPlan] = useState<string | undefined>();
  const [initialBillingPeriod, setInitialBillingPeriod] = useState<
    EPriceTabView | undefined
  >();

  const handleClose = useCallback(() => {
    setPortalOpen(false);
    setInitialPlan(undefined);
    setInitialBillingPeriod(undefined);
    onSelect?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelect]);

  const { portalOpen, setPortalOpen, Portal } = usePortal(
    <StorageAddonModal
      onClose={handleClose}
      analyticsSource={analyticsSource}
      initialPlan={initialPlan}
      initialBillingPeriod={initialBillingPeriod}
    />,
  );

  const handleOpen = useCallback(
    (options?: { plan?: string; billingPeriod?: EPriceTabView }) => {
      if (!isEligible) return;
      onBeforeOpen?.();
      setInitialPlan(options?.plan);
      setInitialBillingPeriod(options?.billingPeriod);
      setPortalOpen(true);
    },
    [isEligible, onBeforeOpen, setPortalOpen],
  );

  if (!isEligible) {
    return <>{children({ onClick: () => {}, isEligible: false })}</>;
  }

  return (
    <>
      {children({ onClick: handleOpen, isEligible: true })}
      {portalOpen && Portal}
    </>
  );
};
